"use client";

import { useState, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { realtimeClient as pusherClient } from "@/app/_lib/realtime-client";
import { getUserAllFeedbacks } from "../lib/actions";
import { startSalesFeedback } from "@/app/dashboard/services-request/lib/actions";
import { Search, Plus, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import PromotionsBanner from "@/app/dashboard/services-request/components/promotions-banner";
import { buildCloudinaryUrl } from "@/app/_lib/cloudinary-url";

type Feedback = Awaited<ReturnType<typeof getUserAllFeedbacks>>[0];

interface Promotion {
  id: string;
  title: string;
  description: string;
  tag: string;
  duration: Date | string;
  image: string;
}

interface Props {
  initialFeedbacks: Feedback[];
  userId?: string | null;
  onNewChat?: () => void;
  promotions?: Promotion[];
  basePath?: string;
  showPromotions?: boolean;
  filterType?: "SALES" | "SUPPORT";
}

function getTitle(f: Feedback): string {
  if (f.subject) return f.subject;
  if ((f as any).coreService?.title) return (f as any).coreService.title;
  if ((f as any).plan?.name) return (f as any).plan.name;
  if ((f as any).solution?.subTitle) return (f as any).solution.subTitle;
  return f.type === "SUPPORT" ? "Support" : "General Inquiry";
}

function TypeBadge({ type }: { type: string }) {
  return (
    <span
      className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
        type === "SUPPORT" ? "bg-blue-50 text-blue-600" : "bg-accent text-primary"
      }`}
    >
      {type === "SUPPORT" ? "Support" : "Service"}
    </span>
  );
}

function ConversationAvatar({
  admin,
  isActive,
}: {
  admin?: { avatar?: string | null; firstName?: string | null; lastName?: string | null } | null;
  isActive: boolean;
}) {
  const avatarUrl = buildCloudinaryUrl(admin?.avatar);

  if (avatarUrl) {
    return (
      <div className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden border-2 border-border">
        <Image
          src={avatarUrl}
          alt={admin?.firstName ?? "Bex"}
          width={40}
          height={40}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  return (
    <div
      className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center ${
        isActive ? "bg-primary text-white" : "bg-accent text-primary"
      }`}
    >
      <MessageCircle size={18} />
    </div>
  );
}

export default function ConversationsSidebar({
  initialFeedbacks,
  userId,
  onNewChat,
  promotions = [],
  basePath = "/dashboard/messages",
  showPromotions = true,
  filterType,
}: Props) {
  const [feedbacks, setFeedbacks] = useState(initialFeedbacks);
  const [search, setSearch] = useState("");
  const pathname = usePathname();
  const router = useRouter();
  useEffect(()=>{
    setFeedbacks(initialFeedbacks)
  },[initialFeedbacks])
  const handleStartConversation = async (title: string) => {
    const res = await startSalesFeedback(
      title,
      `Hi, I'm interested in the "${title}" promotion.`,
    );
    if (res?.feedbackId) {
      router.push(`${basePath}/${res.feedbackId}`);
    }
  };

  useEffect(() => {
    if (!userId) return;
    const channel = pusherClient.subscribe(`user-${userId}`);
    channel.bind("new-notification", async (data: any) => {
      if (data.type === "NEW_MESSAGE" || data.type === "NEW_FEEDBACK") {
        try {
          const fresh = await getUserAllFeedbacks();
          const filtered = filterType
            ? (fresh as any[]).filter((f: any) => f.type === filterType)
            : (fresh as any[]);
          setFeedbacks(filtered as any);
        } catch {}
      }
    });
    return () => { pusherClient.unsubscribe(`user-${userId}`); };
  }, [userId]);

  const filtered = feedbacks.filter((f) => {
    if (!search) return true;
    const title = getTitle(f).toLowerCase();
    const lastMsg = (f as any).messages?.[0]?.content?.toLowerCase() ?? "";
    const q = search.toLowerCase();
    return title.includes(q) || lastMsg.includes(q);
  });

  return (
    <div className="flex flex-col h-full">
      {/* Promotions */}
      {/* {showPromotions && (
        <PromotionsBanner
          promotions={promotions}
          onStartConversation={handleStartConversation}
        />
      )} */}

      {/* Header */}
      <div className="px-4 py-3.5 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-sm">
            {filterType === "SUPPORT" ? "Support" : "Messages"}
          </h2>
          <button
            onClick={onNewChat}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-accent text-primary hover:bg-primary hover:text-white transition-colors"
          >
            <Plus size={14} />
          </button>
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search messages…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-7 pr-3 py-1.5 text-xs bg-muted/5 border border-border rounded-full outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {!userId ? (
          <div className="px-4 py-8 text-center text-xs text-muted-foreground">
            <p>Sign in to see your messages.</p>
            <Link href="/login" className="mt-2 inline-block text-primary hover:underline font-medium">
              Sign in →
            </Link>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-muted-foreground">
            {search ? "No results." : "No conversations yet."}
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {filtered.map((f, i) => {
              const isActive = pathname === `${basePath}/${f.id}`;
              const lastMsg = (f as any).messages?.[0];
              const unread = (f as any)._unreadCount ?? 0;
              const admin = (f as any).admin ?? null;
              const lastTime = f.lastMessageAt
                ? formatDistanceToNow(new Date(f.lastMessageAt), { addSuffix: false })
                : null;

              return (
                <motion.div
                  key={f.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <Link
                    href={`${basePath}/${f.id}`}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-border transition-colors ${
                      isActive ? "bg-[#77b441]/5" : "hover:bg-accent/60"
                    }`}
                  >
                    <ConversationAvatar admin={admin} isActive={isActive} />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <p className="text-sm font-medium truncate">{getTitle(f)}</p>
                        {lastTime && (
                          <span className="text-[10px] text-muted-foreground flex-shrink-0">{lastTime}</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-1 mt-0.5">
                        <p className="text-xs text-muted-foreground truncate">
                          {lastMsg
                            ? lastMsg.isAdmin
                              ? `Bex: ${lastMsg.content ?? "Attachment"}`
                              : lastMsg.content ?? "Attachment"
                            : "No messages yet"}
                        </p>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {/* <TypeBadge type={f.type} /> */}
                          {unread > 0 && (
                            <span className="min-w-[18px] h-[18px] px-1 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                              {unread > 9 ? "9+" : unread}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
